import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, BaseAppConfig } from '@avenir-client-web/models';
import { PreferredLanguageService } from './preferred-language.service';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly supportedLanguages = this.config.supportedLanguages;

  constructor(
    private readonly preferredLanguageService: PreferredLanguageService,
    @Inject(APP_CONFIG) private readonly config: BaseAppConfig
  ) {}

  getLocale(): string {
    const urlLocale = this.getLocaleFromUrl();

    if (this.isLocaleSupported(urlLocale)) {
      return urlLocale;
    } else {
      return this.preferredLanguageService.getPreferredLocale();
    }
  }

  getLanguageFromUrl(): string {
    const urlLocale = this.getLocaleFromUrl();

    return this.isLocaleSupported(urlLocale) ? urlLocale : '';
  }

  switchLanguage(locale: string): void {
    if (!this.isLocaleSupported(locale)) {
      return;
    }

    this.setPreferredLocale(locale);
    this.redirectToNewHref(locale);
  }

  setPreferredLocale(locale: string): void {
    this.preferredLanguageService.setPreferredLocale(locale);

    switch (locale) {
      case 'en':
        registerLocaleData(localeEn);
        break;
      case 'it':
        registerLocaleData(localeIt);
        break;
      case 'fr':
        registerLocaleData(localeFr);
        break;
      default:
        registerLocaleData(localeDe);
        break;
    }
  }

  isLocaleSupported(locale: string): boolean {
    return this.supportedLanguages.includes(locale);
  }

  private getLocaleFromUrl(): string {
    return globalThis.location.pathname.split('/')[1];
  }

  private redirectToNewHref(newLocale: string): void {
    const newPath = this.getPathWithLocale(newLocale);

    globalThis.location.href = `${newPath}${globalThis.location.search}`;
  }

  private getPathWithLocale(newLocale: string): string {
    if (!newLocale || newLocale.length === 0) return '';

    const pathSegments = globalThis.location.pathname
      .split('/')
      .filter(Boolean);

    if (pathSegments.length === 0) return `/${newLocale}`;

    const currentLocale = pathSegments[0];

    const pathWithoutLocale = this.isLocaleSupported(currentLocale)
      ? pathSegments.slice(1).join('/')
      : pathSegments.join('/');

    return pathWithoutLocale
      ? `/${newLocale}/${pathWithoutLocale}`
      : `/${newLocale}`;
  }
}
